import { CarSearchPayload } from '@shared/models/car-search-payload.model';
import { CarSearchResult, CarTransmissionType } from '@shared/models/car-search-response.model';
import { TravelCarHireFilterForm } from '@shared/shared/interfaces/travel-car-hire.interfaces';
import { capitalize } from '@shared/utils/name.lib';
import { CAR_SEARCH_REFERENCE_ID, SELECTED_CAR } from 'app.config';
import * as moment from 'moment';
import * as bookingActions from '../../travel-booking/actions/travel-car-hire-sub-booking.actions';
import * as fromActions from '../actions/car-hire-search.actions';
import { SELECT_CAR } from '../actions/car-hire-search.actions';
import * as searchFormActions from '../actions/travel-search-form.actions';
import { SET_DEFAULT_START_DATE } from '../actions/travel-search-form.actions';

export interface CarHireSearchInitialState {
  carSearch: CarSearchPayload;
  carSearchResults: {
    loading: boolean;
    loaded: boolean;
    results: Array<CarSearchResult>;
    search_reference_id: string;
    validationErrors: Array<any>;
    supplierNames: Array<string>;
    carTypes: Array<string | number>;
    paxCapacities: Array<string | number>;
  };
  visibleCars: {
    results: Array<CarSearchResult>;
    totalPages: number;
    fallbackThumbImageUrl: string;
  };
  selectedCar: CarSearchResult;
}

export const initialState: CarHireSearchInitialState = {
  carSearch: {
    driver_age: 0,
    start_date_full: moment().add(1, 'd').hours(12).minutes(0).seconds(0).milliseconds(0).toString(),
    end_date_full: moment().add(2, 'd').hours(12).minutes(0).seconds(0).milliseconds(0).toString(),
    start_location_id: '',
    end_location_id: '',
    country_code: '',
    selected_start_location_name: '',
    selected_end_location_name: '',
  },
  carSearchResults: {
    loading: false,
    loaded: false,
    results: [],
    search_reference_id: '',
    validationErrors: [],
    supplierNames: [],
    carTypes: [],
    paxCapacities: [],
  },
  visibleCars: {
    results: [],
    totalPages: 0,
    fallbackThumbImageUrl: '',
  },
  selectedCar: <any>{},
};

export function reducer(
  state = initialState,
  action: fromActions.CarHireSearchActions | bookingActions.ClearCarSubBooking | searchFormActions.SetDefaultStartDate
): CarHireSearchInitialState {
  switch (action.type) {
    case SET_DEFAULT_START_DATE: {
      return {
        ...initialState,
        carSearch: {
          ...initialState.carSearch,
          start_date_full: action.payload.startDate,
          end_date_full: action.payload.endDate,
        },
      };
    }

    case fromActions.CLEAR_CAR_SEARCH:
      return initialState;

    case fromActions.RESTORE_CAR_SEARCH_SUCCESS:
      return {
        ...state,
        carSearch: {
          ...state.carSearch,
          ...action.payload.carSearch,
        },
        selectedCar: {
          ...state.carSearch,
          ...action.payload.selectedCar,
        },
        carSearchResults: {
          ...state.carSearchResults,
          search_reference_id: action.payload.carSearchReferenceId,
        },
      };

    case fromActions.CREATE_CAR_SEARCH:
      return {
        ...state,
        carSearch: {
          ...state.carSearch,
          ...action.payload,
        },
      };

    case fromActions.GET_CAR_SEARCH_RESULTS:
      return {
        ...state,
        carSearch: {
          ...state.carSearch,
          ...action.payload,
        },
        carSearchResults: {
          ...state.carSearchResults,
          loading: true,
          loaded: false,
        },
      };

    case fromActions.GET_CAR_SEARCH_RESULTS_SUCCESS: {
      const fileteredResults = filterByInstalmentFeatureSwitch(action.carHireInstalmentsFeatureSwitch, [
        ...action.payload.results,
      ]);

      const results = fileteredResults.sort((carX: CarSearchResult, carY: CarSearchResult) => {
        const sortByPrice = +carX.total_price.amount - +carY.total_price.amount;

        if (sortByPrice) {
          return sortByPrice;
        }

        return 0;
      });

      const { supplierNames, carTypes, paxCapacities } = getAvailableFilterValues(results);

      localStorage.setItem(CAR_SEARCH_REFERENCE_ID, action.payload.search_reference_id);

      return {
        ...state,
        carSearchResults: {
          ...state.carSearchResults,
          search_reference_id: action.payload.search_reference_id,
          results: results,
          loading: false,
          loaded: true,
          supplierNames: supplierNames,
          carTypes: carTypes,
          paxCapacities: paxCapacities,
        },
        visibleCars: {
          ...state.visibleCars,
          fallbackThumbImageUrl: action.payload.fallback_thumb_image_url,
          results: results,
          totalPages: Math.ceil(fileteredResults.length / 12),
        },
        selectedCar: {
          ...state.selectedCar,
          fallbackThumbImageUrl: action.payload.fallback_thumb_image_url,
        },
      };
    }
    case fromActions.GET_CAR_SEARCH_RESULTS_FAILURE:
      return {
        ...state,
        carSearchResults: {
          ...state.carSearchResults,
          loading: false,
          loaded: true,
          validationErrors: action.payload,
        },
      };

    case fromActions.FILTER_CAR_SEARCH_RESULTS: {
      const updatedResults = getFilteredCarResult([...state.carSearchResults.results], action.payload);

      return {
        ...state,
        visibleCars: {
          ...state.visibleCars,
          results: updatedResults,
          totalPages: Math.ceil(updatedResults.length / 12),
        },
      };
    }
    case SELECT_CAR: {
      const selectedCar = state.visibleCars.results.find((car) => {
        return car.booking_token === action.payload.token;
      });
      localStorage.setItem(SELECTED_CAR, JSON.stringify(selectedCar));
      return {
        ...state,
        selectedCar: {
          ...state.selectedCar,
          ...selectedCar,
          isBooking: action.payload.isBooking,
        },
      };
    }
    case bookingActions.CLEAR_CAR_SUB_BOOKING:
      return {
        ...state,
        selectedCar: {
          ...state.selectedCar,
          isBooking: false,
        },
      };
    default:
      return state;
  }
}

function filterByInstalmentFeatureSwitch(instalmentsSwitch: boolean, results: CarSearchResult[]): CarSearchResult[] {
  return results.filter(
    (car: CarSearchResult) =>
      !car.instalment_payments_available || (car.instalment_payments_available && instalmentsSwitch)
  );
}

function getAvailableFilterValues(results: Array<CarSearchResult>): {
  supplierNames: string[];
  carTypes: Array<string | number>;
  paxCapacities: number[];
} {
  const suppliers: string[] = [];
  const paxCapacities: number[] = [];
  let carTypes: Array<string | number> = [];

  for (const car of results) {
    if (!suppliers.includes(capitalize(car.supplier_name))) {
      suppliers.push(capitalize(car.supplier_name));
    }

    if (!paxCapacities.includes(car.pax_capacity)) {
      paxCapacities.push(car.pax_capacity);
    }

    if (Array.isArray(car.car_category_ids) && car.car_category_ids.length > 0) {
      carTypes = [...carTypes, ...car.car_category_ids];
    }
  }

  return {
    supplierNames: suppliers,
    carTypes: [...new Set(carTypes)],
    paxCapacities: paxCapacities.sort((a, b) => a - b),
  };
}

export function getFilteredCarResult(results: CarSearchResult[], filters: TravelCarHireFilterForm): CarSearchResult[] {
  const automaticOnly = filters?.automatic_only !== undefined ? filters.automatic_only : null;

  const carTypesArray = filters?.car_type?.length ? JSON.parse(filters.car_type) : null;

  const paxCapacity = filters?.pax_capacity !== undefined ? filters.pax_capacity : null;

  const supplier = filters?.supplier !== undefined ? filters.supplier : null;

  const instalmentPaymentsAvailable =
    filters?.instalment_payments_available !== undefined ? filters.instalment_payments_available : null;

  const filteredResults = results
    .filter((car: CarSearchResult) => {
      const automaticOnlyFilter =
        automaticOnly !== null ? car.transmission.type === CarTransmissionType.AUTOMATIC : true;

      const carTypeFilter = carTypesArray
        ? carTypesArray.some((type: number) => car.car_category_ids.includes(type))
        : true;

      const paxCapacityFilter =
        paxCapacity !== null ? car.pax_capacity === paxCapacity || car.pax_capacity > paxCapacity : true;

      const supplierFilter = supplier !== null ? capitalize(car.supplier_name) === supplier : true;

      const instalmentPaymentsAvailableFilter =
        instalmentPaymentsAvailable !== null ? car.instalment_payments_available === instalmentPaymentsAvailable : true;

      return (
        automaticOnlyFilter && carTypeFilter && paxCapacityFilter && supplierFilter && instalmentPaymentsAvailableFilter
      );
    })
    .sort((carX: any, carY: any) => {
      const sortByPrice = carX.total_price.amount - carY.total_price.amount;

      if (sortByPrice) {
        return sortByPrice;
      }
    });

  return filteredResults;
}

export const getSearchData = (state: CarHireSearchInitialState) => state.carSearch;
export const getResults = (state: CarHireSearchInitialState) => state.carSearchResults;
export const getSupplierNames = (state: CarHireSearchInitialState) => state.carSearchResults.supplierNames;
export const getCarTypes = (state: CarHireSearchInitialState) => state.carSearchResults.carTypes;
export const getPaxCapacities = (state: CarHireSearchInitialState) => state.carSearchResults.paxCapacities;
export const getVisibleCars = (state: CarHireSearchInitialState) => state.visibleCars;
export const getCarSearchResultsLoading = (state: CarHireSearchInitialState) => state.carSearchResults.loading;
export const getCarSearchResultsLoaded = (state: CarHireSearchInitialState) => state.carSearchResults.loaded;
export const getSelectedCar = (state: CarHireSearchInitialState) => state.selectedCar;
export const getTotalPages = (state: CarHireSearchInitialState) => state.visibleCars.totalPages;
